import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import type { JobStage } from '../lib/api';
import styles from './ProgressCircle.module.css';

interface Props {
  stage: JobStage;
  /** Optional override on the percent; otherwise derived from stage. */
  percent?: number;
  size?: number;
  className?: string;
}

const STAGE_PERCENT: Record<JobStage, number> = {
  queued: 5,
  extract: 33,
  score: 66,
  synthesize: 92,
  done: 100,
  error: 100,
};

const STAGE_LABEL: Record<JobStage, string> = {
  queued: 'Starting',
  extract: 'Extracting',
  score: 'Scoring',
  synthesize: 'Synthesizing',
  done: 'Complete',
  error: 'Error',
};

export function ProgressCircle({ stage, percent, size = 120, className }: Props) {
  // Smooth the displayed percent toward the target so each stage transition
  // animates rather than jumps.
  const target = percent ?? STAGE_PERCENT[stage];
  const [displayed, setDisplayed] = useState(target);

  useEffect(() => {
    if (displayed === target) return;
    const id = window.setInterval(() => {
      setDisplayed((cur) => {
        const delta = target - cur;
        if (Math.abs(delta) < 0.5) return target;
        return cur + delta * 0.18;
      });
    }, 16);
    return () => window.clearInterval(id);
  }, [target, displayed]);

  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - displayed / 100);
  const isDone = stage === 'done';
  const isError = stage === 'error';

  return (
    <div
      className={[styles.wrap, className].filter(Boolean).join(' ')}
      style={{ width: size, height: size }}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(displayed)}
      aria-label={`${STAGE_LABEL[stage]} — ${Math.round(displayed)}%`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={styles.svg}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--tia-grey-100)"
          strokeWidth={stroke}
          className={styles.track}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={isError ? 'var(--tia-negative)' : isDone ? 'var(--tia-positive)' : 'var(--tia-navy)'}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className={styles.fill}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className={styles.center}>
        {isDone ? (
          <Check size={32} strokeWidth={2.5} className={styles.checkIcon} aria-hidden="true" />
        ) : (
          <>
            <span className={[styles.percent, 'mono'].join(' ')}>{Math.round(displayed)}<span className={styles.percentSign}>%</span></span>
            <span className={styles.label}>{STAGE_LABEL[stage]}</span>
          </>
        )}
      </div>
    </div>
  );
}
